import React, { useState, useCallback, useEffect, useRef } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import FormCard from "../FormComponents/FormCard";
import FormGroup from "../FormComponents/FormGroup";
import FormGrid from "../FormComponents/FormGrid";
import AlertBanner from "../FormComponents/AlertBanner";
import ExhibitorInfoBar from "../FormComponents/ExhibitorInfoBar";
import SignatureSection from "../FormComponents/SignatureSection";
import PaymentSection from "../FormComponents/PaymentSection";
import Icon from "../Icons/Icon";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Step 8: Marketing Opportunities
 *
 * This step allows exhibitors to select and purchase various marketing opportunities:
 * - Show Guide advertisements (Full, Half, Quarter page)
 * - Digital marketing (Website banners, Email features, Social media)
 * - Tiered discount system based on total spend
 */
const Step8 = ({
  exhibitorData,
  initialContract,
  onSave,
  onDataChange,
  disabled = false,
  errors = {},
}) => {
  const [formData, setFormData] = useState({
    marketingContactName: initialContract?.marketingContactName || "",
    marketingContactPhone: initialContract?.marketingContactPhone || "",
    marketingContactEmail: initialContract?.marketingContactEmail || "",
    showGuideFull: initialContract?.showGuideFull ?? 0,
    showGuideHalf: initialContract?.showGuideHalf ?? 0,
    showGuideQuarter: initialContract?.showGuideQuarter ?? 0,
    showGuideOptOut: initialContract?.showGuideOptOut ?? false,
    digitalCarousel: initialContract?.digitalCarousel ?? 0,
    digitalPopular: initialContract?.digitalPopular ?? 0,
    digitalCategory: initialContract?.digitalCategory ?? 0,
    digitalSocial: initialContract?.digitalSocial ?? 0,
    digitalOutlookFeatured: initialContract?.digitalOutlookFeatured ?? 0,
    digitalOutlookBanner: initialContract?.digitalOutlookBanner ?? 0,
    digitalEdalFeatured: initialContract?.digitalEdalFeatured ?? 0,
    digitalEdalBanner: initialContract?.digitalEdalBanner ?? 0,
    digitalShowFeatured: initialContract?.digitalShowFeatured ?? 0,
    digitalShowBanner: initialContract?.digitalShowBanner ?? 0,
    digitalEntrance: initialContract?.digitalEntrance ?? 0,
    digitalPodcast: initialContract?.digitalPodcast ?? 0,
    selectionsApproved: initialContract?.selectionsApproved ?? false,
    interestedInSeasonMarketing: initialContract?.interestedInSeasonMarketing ?? false,
    interestedShowMarketing: initialContract?.interestedShowMarketing ?? false,
    optNotToParticipate: initialContract?.optNotToParticipate ?? false,
    paymentMethod: initialContract?.paymentMethod || "",
    invoiceAddressText: initialContract?.invoiceAddressText || "",
    signatureName: initialContract?.signatureName || "",
    signatureDate: initialContract?.signatureDate || "",
    signatureAcknowledge: initialContract?.signatureAcknowledge ?? false,
  });

  const [emailFormatError, setEmailFormatError] = useState("");

  const formDataRef = useRef(formData);
  const autoSaveTimerRef = useRef(null);
  const hasChangedRef = useRef(false);

  // Keep ref in sync
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  // Notify parent of data changes
  useEffect(() => {
    if (onDataChange) onDataChange(formData);
  }, [formData, onDataChange]);

  // Auto-save with debounce
  useEffect(() => {
    if (!hasChangedRef.current || disabled) return;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      if (onSave) onSave(formDataRef.current);
    }, 3000);
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, [formData, onSave, disabled]);

  // Save on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
      if (hasChangedRef.current && onSave) {
        onSave(formDataRef.current);
      }
    };
  }, [onSave]);

  const updateField = useCallback((field, value) => {
    hasChangedRef.current = true;
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const hasErrors = Object.keys(errors).length > 0;

  const showGuideQtys = {
    full: formData.showGuideFull,
    half: formData.showGuideHalf,
    quarter: formData.showGuideQuarter,
  };

  const setShowGuideQtys = (updateFn) => {
    const updated = updateFn(showGuideQtys);
    if (updated.full !== undefined) updateField("showGuideFull", updated.full);
    if (updated.half !== undefined) updateField("showGuideHalf", updated.half);
    if (updated.quarter !== undefined) updateField("showGuideQuarter", updated.quarter);
  };

  const digitalQtys = {
    carousel: formData.digitalCarousel,
    popular: formData.digitalPopular,
    category: formData.digitalCategory,
    social: formData.digitalSocial,
    outlookFeatured: formData.digitalOutlookFeatured,
    outlookBanner: formData.digitalOutlookBanner,
    edalFeatured: formData.digitalEdalFeatured,
    edalBanner: formData.digitalEdalBanner,
    showFeatured: formData.digitalShowFeatured,
    showBanner: formData.digitalShowBanner,
    entrance: formData.digitalEntrance,
    podcast: formData.digitalPodcast,
  };

  const setDigitalQtys = (updateFn) => {
    const updated = updateFn(digitalQtys);
    Object.keys(updated).forEach((key) => {
      const capitalizedKey = `digital${key.charAt(0).toUpperCase()}${key.slice(1)}`;
      updateField(capitalizedKey, updated[key]);
    });
  };

  const showGuidePrices = { full: 895, half: 595, quarter: 495 };
  const digitalItems = [
    { key: "carousel", label: "Arett.com Home page carousel banner", price: 450 },
    { key: "popular", label: "Arett.com Home page – Popular category", price: 250 },
    { key: "category", label: "Arett.com – Category Takeover", price: 1000 },
    { key: "social", label: "Social Media Post", price: 99 },
    { key: "outlookFeatured", label: "Outlook Email – Featured article", price: 300 },
    { key: "outlookBanner", label: "Outlook Email – Banner Ad", price: 300 },
    { key: "edalFeatured", label: "Edeal Email – Featured Item", price: 300 },
    { key: "edalBanner", label: "Edeal Email – Banner Ad", price: 300 },
    { key: "showFeatured", label: "Show Email – Featured Item", price: 300 },
    { key: "showBanner", label: "Show Email – Banner Ad", price: 300 },
    { key: "podcast", label: "Arett Podcast", price: 600 },
  ];

  const showGuideTotal = Object.entries(showGuideQtys).reduce(
    (s, [k, v]) => s + showGuidePrices[k] * v,
    0,
  );
  const digitalTotal = digitalItems.reduce(
    (s, item) => s + item.price * (digitalQtys[item.key] || 0),
    0,
  );
  const combinedTotal = showGuideTotal + digitalTotal;
  const discount =
    digitalTotal >= 3000
      ? digitalTotal * 0.3
      : digitalTotal >= 2000
        ? digitalTotal * 0.25
        : digitalTotal >= 1500
          ? digitalTotal * 0.2
          : 0;
  const discountLabel =
    digitalTotal >= 3000
      ? "Tier 3: Spend $3000+ – 30% digital discount has been applied."
      : digitalTotal >= 2000
        ? "Tier 2: Spend $2000 to $2999: 25% digital discount has been applied."
        : digitalTotal >= 1500
          ? "Tier 1: Spend $1500 to $1999: 20% digital discount has been applied."
          : "No discount applied yet.";

  return (
    <fieldset disabled={disabled} style={{ border: "none", padding: 0, margin: 0 }}>
      <div className="form-page">
        <div className="form-page-header">
          <h2 className="form-page-title">Marketing Opportunities</h2>
        </div>

        {hasErrors && (
          <AlertBanner type="warning">
            <strong>Please fix the following errors:</strong>{" "}
            {Object.values(errors).join(", ")}
          </AlertBanner>
        )}

        <AlertBanner type="info">
          INSTRUCTIONS: PLEASE FILL OUT ALL FIELDS – ENTER N/A WHERE NOT
          APPLICABLE
        </AlertBanner>

        <ExhibitorInfoBar exhibitorData={exhibitorData} />

        <FormCard
          title="Marketing Contact Information"
          icon={<Icon name="phone" size={14} />}
        >
          <p style={{ fontSize: 14, marginBottom: 16 }}>
            As an Arett partner, we are offering you a multitude of marketing
            opportunities with <strong>Greensmith</strong> – Arett's preferred
            advertising agency.
          </p>
          <FormGrid cols={3}>
            <FormGroup label="Marketing Contact Name" error={errors.marketingContactName}>
              <input
                className={`form-input ${errors.marketingContactName ? "input-error" : ""}`}
                value={formData.marketingContactName}
                onChange={(e) => updateField("marketingContactName", e.target.value)}
              />
            </FormGroup>
            <FormGroup label="Marketing Contact Phone" error={errors.marketingContactPhone}>
              <div className="form-phone-input">
                <PhoneInput
                  country="us"
                  value={formData.marketingContactPhone}
                  onChange={(val) => updateField("marketingContactPhone", val)}
                  enableSearch
                  disableSearchIcon
                  inputProps={{ name: "marketingContactPhone" }}
                />
              </div>
            </FormGroup>
            <FormGroup label="Marketing Contact Email" error={errors.marketingContactEmail || emailFormatError}>
              <input
                className={`form-input ${errors.marketingContactEmail || emailFormatError ? "input-error" : ""}`}
                value={formData.marketingContactEmail}
                onChange={(e) => {
                  updateField("marketingContactEmail", e.target.value);
                  if (emailFormatError) setEmailFormatError("");
                }}
                onBlur={(e) => {
                  const val = e.target.value.trim();
                  if (val && !EMAIL_REGEX.test(val)) {
                    setEmailFormatError("Please enter a valid email address");
                  } else {
                    setEmailFormatError("");
                  }
                }}
              />
            </FormGroup>
          </FormGrid>
        </FormCard>

        <div className="marketing-table-wrapper">
          <div className="marketing-table-header">
            CREATE YOUR OPEN HOUSE MARKETING PROGRAM
          </div>
          <div className="tier-box" style={{ borderRadius: 0, margin: 0 }}>
            <h3>The more you choose the more you save</h3>
            <p className="tier-item">1. Select Qty for each option</p>
            <p className="tier-item">
              2. View your discount and total cost at the bottom
            </p>
            <div
              style={{
                marginTop: 12,
                borderTop: "1px solid rgba(255,255,255,0.2)",
                paddingTop: 12,
              }}
            >
              <p className="tier-item">
                <strong>Tier 1:</strong> Spend $1500 to $1999: 20% discount
              </p>
              <p className="tier-item">
                <strong>Tier 2:</strong> Spend $2000 to $2999: 25% discount
              </p>
              <p className="tier-item">
                <strong>Tier 3:</strong> Spend $3000+: 30% discount
              </p>
            </div>
            <p className="tier-note">
              No discounts on the Show Guide or Sponsorships.
            </p>
          </div>

          <div style={{ padding: "0 0" }}>
            <div
              style={{
                padding: "12px 16px",
                background: "var(--neutral-50)",
                fontWeight: 700,
                fontSize: 13,
                borderBottom: "1px solid var(--neutral-200)",
              }}
            >
              Show Guide:{" "}
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 400,
                  color: "var(--neutral-500)",
                }}
              >
                (material deadline for ads is July 26th, 2026. This is printed and
                digital)
              </span>
            </div>
            <table className="contract-table">
              <thead>
                <tr>
                  <th>Qty</th>
                  <th>Ad Option</th>
                  <th>Internal Code 9002</th>
                  <th>Unit Cost</th>
                  <th>Total Cost</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { key: "full", label: 'Full Page Ad (7.65" x 10" H)', price: 895 },
                  { key: "half", label: 'Half Page Ad (7.65" x 4.95" H)', price: 595 },
                  { key: "quarter", label: 'Quarter Page Ad (3.75" x 4.95" H)', price: 495 },
                ].map((item) => (
                  <tr key={item.key}>
                    <td>
                      <input
                        className="form-input"
                        type="number"
                        min="0"
                        value={showGuideQtys[item.key]}
                        onFocus={(e) => e.target.select()}
                        onChange={(e) =>
                          setShowGuideQtys((p) => ({
                            ...p,
                            [item.key]: parseInt(e.target.value) || 0,
                          }))
                        }
                        style={{ width: 70 }}
                      />
                    </td>
                    <td>
                      <label className="custom-checkbox">
                        <input
                          type="checkbox"
                          checked={showGuideQtys[item.key] > 0}
                          onChange={(e) =>
                            setShowGuideQtys((p) => ({
                              ...p,
                              [item.key]: e.target.checked ? 1 : 0,
                            }))
                          }
                        />
                        <span>{item.label}</span>
                      </label>
                    </td>
                    <td>7316-0005</td>
                    <td>${item.price}</td>
                    <td>
                      <strong>
                        ${(item.price * showGuideQtys[item.key]).toLocaleString()}
                      </strong>
                    </td>
                  </tr>
                ))}
                <tr>
                  <td colSpan={2}>
                    <label className="custom-checkbox">
                      <input
                        type="checkbox"
                        checked={formData.showGuideOptOut}
                        onChange={(e) => updateField("showGuideOptOut", e.target.checked)}
                      />
                      <span>Opt Out</span>
                    </label>
                  </td>
                  <td colSpan={3}></td>
                </tr>
              </tbody>
            </table>

            <div
              style={{
                padding: "12px 16px",
                background: "var(--neutral-50)",
                fontWeight: 700,
                fontSize: 13,
                borderBottom: "1px solid var(--neutral-200)",
                borderTop: "1px solid var(--neutral-200)",
              }}
            >
              Marketing Opportunities
            </div>
            <table className="contract-table">
              <thead>
                <tr>
                  <th>Qty</th>
                  <th>Marketing Opportunity</th>
                  <th>EA Price</th>
                  <th>Sub Total</th>
                </tr>
              </thead>
              <tbody>
                {digitalItems.map((item) => (
                  <tr key={item.key}>
                    <td>
                      <input
                        className="form-input"
                        type="number"
                        min="0"
                        value={digitalQtys[item.key]}
                        onFocus={(e) => e.target.select()}
                        onChange={(e) =>
                          setDigitalQtys((p) => ({
                            ...p,
                            [item.key]: parseInt(e.target.value) || 0,
                          }))
                        }
                        style={{ width: 70 }}
                      />
                    </td>
                    <td>{item.label}</td>
                    <td>${item.price}</td>
                    <td>
                      <strong>
                        ${(item.price * digitalQtys[item.key]).toLocaleString()}
                      </strong>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ padding: "16px 20px" }}>
              <div className="discount-row">
                <span>Sub Total Show Guide:</span>
                <strong>${showGuideTotal.toLocaleString()}</strong>
              </div>
              <div className="discount-row">
                <span>Sub Total Digital:</span>
                <strong>${digitalTotal.toLocaleString()}</strong>
              </div>
              {discount > 0 && (
                <div className="discount-row discount">
                  <span>Digital Discount ({discountLabel})</span>
                  <strong>-${discount.toLocaleString()}</strong>
                </div>
              )}
              <div className="discount-row total">
                <span>Total Cost of All Marketing Programs:</span>
                <strong>${(combinedTotal - discount).toLocaleString()}</strong>
              </div>
            </div>

            <AlertBanner type="info" style={{ margin: "0 16px 16px" }}>
              Note: Please note all design submitted is subject to your Arett
              Merchant approval. Any ad materials not received by the deadline
              requested will be created at the discretion of your Arett Merchant
              and Greensmith, a charge of $199 per design will added to your
              invoice.
            </AlertBanner>

            <div style={{ padding: "0 16px 16px" }}>
              <div className="checkbox-group vertical">
                <label className="custom-checkbox">
                  <input
                    type="checkbox"
                    checked={formData.selectionsApproved}
                    onChange={(e) => updateField("selectionsApproved", e.target.checked)}
                  />
                  <span>Above Selections Approved</span>
                </label>
                <label className="custom-checkbox">
                  <input
                    type="checkbox"
                    checked={formData.interestedInSeasonMarketing}
                    onChange={(e) => updateField("interestedInSeasonMarketing", e.target.checked)}
                  />
                  <span>
                    I am interested in in-season marketing.{" "}
                    <a className="link" href="https://www.dropbox.com/scl/fi/0vs73psqxs70ecf7ysw5c/2026-Arett-Greensmith-Vendor-Partner-Marketing-Contract.xltx?rlkey=x35ejsbps1ts32yxsxg0oldu7&st=ehuzn8l3&dl=0" target="_blank" rel="noopener noreferrer">
                      Click Here
                    </a>{" "}
                    to download excel order form.
                  </span>
                </label>
                <label className="custom-checkbox">
                  <input
                    type="checkbox"
                    checked={formData.interestedShowMarketing}
                    onChange={(e) => updateField("interestedShowMarketing", e.target.checked)}
                  />
                  <span>I am interested Show marketing. Please contact me</span>
                </label>
                <label className="custom-checkbox">
                  <input
                    type="checkbox"
                    checked={formData.optNotToParticipate}
                    onChange={(e) => updateField("optNotToParticipate", e.target.checked)}
                  />
                  <span>Opt Not To Participate</span>
                </label>
              </div>
              {errors.selectionsGroup && (
                <span className="field-error" style={{ marginTop: 8, display: "block" }}>{errors.selectionsGroup}</span>
              )}
            </div>
          </div>
        </div>

        <PaymentSection
          paymentMethod={formData.paymentMethod}
          invoiceAddressText={formData.invoiceAddressText}
          onPaymentMethodChange={(val) => updateField("paymentMethod", val)}
          onAddressChange={(val) => updateField("invoiceAddressText", val)}
        />
        {errors.paymentMethod && (
          <span className="field-error" style={{ marginTop: 8, display: "block" }}>{errors.paymentMethod}</span>
        )}
        <SignatureSection
          legal={false}
          name={formData.signatureName}
          date={formData.signatureDate}
          acknowledge={formData.signatureAcknowledge}
          onNameChange={(name) => updateField("signatureName", name)}
          onDateChange={(date) => updateField("signatureDate", date)}
          onAcknowledge={(v) => updateField("signatureAcknowledge", v)}
        />
        {(errors.signatureName || errors.signatureDate) && (
          <div className="step-error-summary" style={{ marginTop: 8 }}>
            {errors.signatureName && <span className="field-error">{errors.signatureName}</span>}
            {errors.signatureDate && <span className="field-error">{errors.signatureDate}</span>}
          </div>
        )}
      </div>
    </fieldset>
  );
};

export default Step8;
